export const PRIMARY_COLOR = "#bb1ccc";
export const PRIMARY_DARK = "#000080";

export const buttonStyles = {
  contained: {
    backgroundColor: PRIMARY_COLOR,
    color: "#fff",
    boxShadow: "none",
    "&:hover": {
      backgroundColor: PRIMARY_DARK,
      boxShadow: "none",
    },
    "&.Mui-disabled": {
      backgroundColor: "grey.400",
      color: "#f5f5f5",
    },
  },
  text: {
    color: PRIMARY_COLOR,
    fontWeight: 600,
    "&:hover": {
      backgroundColor: "rgba(0,0,160,0.08)",
    },
  },
  outlined: {
    color: PRIMARY_COLOR,
    borderColor: PRIMARY_COLOR,
    "&:hover": {
      borderColor: PRIMARY_DARK,
      backgroundColor: "rgba(0,0,160,0.04)",
    },
  },
};

export const fallbackUser = {
  id: "demo-user",
  name: "Demo User",
  email: "demo.user@example.com",
  role: "Admin",
};

export const getStoredUser = () => {
  if (typeof window === "undefined") return fallbackUser;

  try {
    const profileRaw =
      window.localStorage.getItem("userProfile") ||
      window.localStorage.getItem("userDetails");
    const profile = profileRaw ? JSON.parse(profileRaw) : {};

    const id =
      profile?.userId ||
      profile?.id ||
      window.localStorage.getItem("userId") ||
      window.localStorage.getItem("userID") ||
      fallbackUser.id;
    const name =
      profile?.name ||
      profile?.userName ||
      window.localStorage.getItem("userName") ||
      fallbackUser.name;
    const email =
      profile?.email ||
      profile?.userEmail ||
      window.localStorage.getItem("userEmail") ||
      fallbackUser.email;
    const role =
      profile?.role || window.localStorage.getItem("role") || fallbackUser.role;

    return { id, name, email, role };
  } catch (error) {
    return fallbackUser;
  }
};

export const STATUS_OPTIONS = [
  { label: "All Statuses", value: "ALL" },
  { label: "Pending Review", value: "PENDING_REVIEW" },
  { label: "Under Review", value: "UNDER_REVIEW" },
  { label: "Approved", value: "APPROVED" },
  { label: "In Progress", value: "IN_PROGRESS" },
  { label: "Completed", value: "COMPLETED" },
  { label: "Rejected", value: "REJECTED" },
];

export const ROADMAP_STATUS_OPTIONS = [
  { label: "All Statuses", value: "ALL" },
  { label: "Not Started", value: "NOT_STARTED" },
  { label: "In Progress", value: "IN_PROGRESS" },
  { label: "Completed", value: "COMPLETED" },
];

export const CATEGORY_OPTIONS = [
  {
    label: "All Categories",
    value: "ALL",
    description: "View every feature request",
  },
  {
    label: "New Feature",
    value: "NEW_FEATURE",
    description: "Request a new capability",
  },
  {
    label: "Enhancement",
    value: "IMPROVEMENT",
    description: "Improve existing functionality",
  },
  {
    label: "Bug Fix",
    value: "BUG",
    description: "Report something broken",
  },
  {
    label: "UI/UX",
    value: "UI_UX",
    description: "Visual or usability improvements",
  },
  {
    label: "Performance",
    value: "PERFORMANCE",
    description: "Speed or stability updates",
  },
  {
    label: "Security",
    value: "SECURITY",
    description: "Authentication, authorization, or compliance updates",
  },
  {
    label: "Other",
    value: "OTHER",
    description: "Anything else",
  },
  {
    label: "Onboard Product",
    value: "ONBOARD_PRODUCT",
    description: "Requests related to onboarding a product",
  },
  {
    label: "Register Product",
    value: "REGISTER_PRODUCT",
    description: "Requests related to register a product",
  },
  {
    label: "Epic",
    value: "EPIC",
    description: "Requests related to Feature",
  }
];


const STATUS_TO_FRONTEND = {
  "Pending Review": "PENDING_REVIEW",
  "Under Review": "UNDER_REVIEW",
  "Approved": "APPROVED",
  "In Progress": "IN_PROGRESS",
  "Completed": "COMPLETED",
  "Rejected": "REJECTED",
};

const STATUS_TO_ROADMAPFRONTEND = {
  "Pending Review": "PENDING_REVIEW",
  "Under Review": "UNDER_REVIEW",
  "Not Started": "NOT_STARTED",
  "Approved": "APPROVED",
  "In Progress": "IN_PROGRESS",
  "Completed": "COMPLETED",
  "Rejected": "REJECTED",
};

const FRONTEND_TO_STATUS = Object.entries(STATUS_TO_FRONTEND).reduce(
  (acc, [api, ui]) => ({ ...acc, [ui]: api }),
  {}
);

const FRONTEND_TO_ROADMAP_STATUS = Object.entries(STATUS_TO_ROADMAPFRONTEND).reduce(
  (acc, [api, ui]) => ({ ...acc, [ui]: api }),
  {}
);

const CATEGORY_TO_FRONTEND = {
  "New Feature": "NEW_FEATURE",
  Enhancement: "IMPROVEMENT",
  "Bug Fix": "BUG",
  "Bug Report": "BUG",
  "UI/UX": "UI_UX",
  "UI / UX": "UI_UX",
  Performance: "PERFORMANCE",
  Security: "SECURITY",
  Other: "OTHER",
  "Onboard Product": "ONBOARD_PRODUCT",
  "Register Product": "REGISTER_PRODUCT",
  "Epic": "EPIC"
};

const FRONTEND_TO_CATEGORY = {
  NEW_FEATURE: "New Feature",
  IMPROVEMENT: "Enhancement",
  BUG: "Bug Fix",
  UI_UX: "UI/UX",
  PERFORMANCE: "Performance",
  SECURITY: "Security",
  OTHER: "Other",
  ONBOARD_PRODUCT: "Onboard Product",
};

export const mapApiStatusToFrontend = (apiStatus) =>
  STATUS_TO_FRONTEND[apiStatus] || apiStatus;

export const mapFrontendStatusToApi = (frontendStatus) =>
  FRONTEND_TO_STATUS[frontendStatus] || frontendStatus;

export const mapFrontendRoadmapStatusToApi = (frontendStatus) =>
  FRONTEND_TO_ROADMAP_STATUS[frontendStatus] || frontendStatus;


export const mapApiCategoryToFrontend = (apiCategory) =>
  CATEGORY_TO_FRONTEND[apiCategory] || apiCategory;

export const mapFrontendCategoryToApi = (frontendCategory) =>
  FRONTEND_TO_CATEGORY[frontendCategory] || frontendCategory;

// Normalize priority from lowercase to uppercase (API returns "low", "medium", "high")
const normalizePriority = (priority) => {
  if (!priority) return "MEDIUM";
  const upper = priority.toUpperCase();
  return ["HIGH", "MEDIUM", "LOW"].includes(upper) ? upper : "MEDIUM";
};

export const transformApiItemToFrontend = (apiItem) => ({
  id: apiItem.id,
  title: apiItem.title,
  description: apiItem.description,
  projectName: apiItem.project_name || "",
  priority: normalizePriority(apiItem.priority),
  eta: apiItem.eta || "", // Handle null values
  category: mapApiCategoryToFrontend(apiItem.category),
  status: mapApiStatusToFrontend(apiItem.status),
  submittedBy: apiItem.created_by,
  submittedByEmail: "",
  submittedById: "",
  createdAt: apiItem.created_date,
  upvotes: apiItem.upvote_count,
  hasVoted: apiItem.liked_by_user,
  assignee: apiItem.assigned_to || "",
  commentCount: apiItem?.comment_count || 0,
  comments: []
});


